"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  KanbanSquare, Table as TableIcon, BellRing, BarChart3, ShieldCheck, Bot, Search,
  Users, Wallet, UserCheck, Layers, TrendingUp, Landmark, FileClock, Percent,
  MessageCircle, Phone, Eye, Zap, ShieldAlert,
} from "lucide-react";

import {
  cloneSeedLeads, computeStats, BROKING_STAGES, BROKING_SEGMENTS, BRANCHES,
  RELATIONSHIP_MANAGERS, CRM_ROLES, SEED_REMINDERS, SEED_CALL_LOG, INCOMING_WHATSAPP_POOL,
  rmById, branchById, rmsForBranch, maskPan, formatINRCompact,
  type BrokingLead, type CrmRole, type Reminder, type IncomingWhatsApp, type ActivityEvent,
} from "@/lib/demos/stockBrokingData";

import { StockBrokingTopBar } from "./StockBrokingTopBar";
import { StockBrokingKanban } from "./StockBrokingKanban";
import { StockBrokingStageModal } from "./StockBrokingStageModal";
import { StockBrokingDetailModal } from "./StockBrokingDetailModal";
import { StockBrokingWhatsAppDrawer, type ChatContact } from "./StockBrokingWhatsAppDrawer";
import { IncomingWhatsAppToast, playIncomingChime } from "./IncomingWhatsAppToast";
import { StockBrokingReportsTab } from "./StockBrokingReportsTab";
import { StockBrokingComplianceTab } from "./StockBrokingComplianceTab";
import { AiVoiceAssistantTab } from "./AiVoiceAssistantTab";
import { RemindersPanel } from "./RemindersPanel";

type ViewMode = "kanban" | "table" | "reminders" | "reports" | "compliance" | "ai-voice";

const SELF_RM = "RM-101"; // the "logged-in" RM when role = RM

const PRIORITY_STYLE: Record<BrokingLead["priority"], string> = {
  HOT: "bg-rose-500/15 text-rose-700 border-rose-500/25",
  WARM: "bg-amber-500/15 text-amber-700 border-amber-500/25",
  COLD: "bg-blue-500/15 text-blue-700 border-blue-500/25",
};

export function StockBrokingCrmDashboard() {
  const [leads, setLeads] = useState<BrokingLead[]>(() => cloneSeedLeads());
  const [reminders, setReminders] = useState<Reminder[]>(() => SEED_REMINDERS.map((r) => ({ ...r })));
  const [role, setRole] = useState<CrmRole>("Admin");
  const [view, setView] = useState<ViewMode>("kanban");
  const [isResetting, setIsResetting] = useState(false);
  const [roundRobin, setRoundRobin] = useState(false);
  const roundRobinPtr = useRef(0);

  // Filters
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("ALL");
  const [rmFilter, setRmFilter] = useState("ALL");
  const [segmentFilter, setSegmentFilter] = useState("ALL");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [priorityFilter, setPriorityFilter] = useState("ALL");

  // Modals / drawers
  const [selectedLead, setSelectedLead] = useState<BrokingLead | null>(null);
  const [stageModal, setStageModal] = useState<{ lead: BrokingLead; target: string } | null>(null);
  const [chat, setChat] = useState<ChatContact | null>(null);
  const [incoming, setIncoming] = useState<IncomingWhatsApp | null>(null);
  const incomingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentActor = role === "RM" ? `${rmById(SELF_RM)?.name} (RM)` : role;

  /* ------------------------ real-time WhatsApp popup ------------------------ */
  const fireIncoming = useCallback((withSound: boolean) => {
    const msg = INCOMING_WHATSAPP_POOL[Math.floor(Math.random() * INCOMING_WHATSAPP_POOL.length)];
    setIncoming(msg);
    if (withSound) playIncomingChime();
    if (incomingTimer.current) clearTimeout(incomingTimer.current);
    incomingTimer.current = setTimeout(() => setIncoming(null), 12000);
  }, []);

  useEffect(() => {
    // Auto-fire a simulated incoming WhatsApp every 30s (manual "Simulate incoming" still works anytime).
    const id = setInterval(() => fireIncoming(true), 30000);
    return () => {
      clearInterval(id);
      if (incomingTimer.current) clearTimeout(incomingTimer.current);
    };
  }, [fireIncoming]);

  function openChatFromIncoming(msg: IncomingWhatsApp) {
    setChat({ name: msg.name, phone: msg.phone, client_code: msg.clientCode, seedInbound: msg.preview });
    setIncoming(null);
  }

  /* ------------------------------ lead updates ------------------------------ */
  const appendTimeline = useCallback(
    (lead: BrokingLead, ev: Omit<ActivityEvent, "id" | "ts">): BrokingLead => ({
      ...lead,
      updated_at: new Date().toISOString().slice(0, 10),
      timeline: [{ id: `t-${Date.now()}`, ts: "Just now", ...ev }, ...lead.timeline],
    }),
    []
  );

  const patchLead = useCallback(
    (id: number, patch: Partial<BrokingLead>, ev?: Omit<ActivityEvent, "id" | "ts">) => {
      setLeads((prev) =>
        prev.map((l) => {
          if (l.id !== id) return l;
          const merged = { ...l, ...patch };
          return ev ? appendTimeline(merged, ev) : merged;
        })
      );
    },
    [appendTimeline]
  );

  function directMove(id: number, target: string) {
    patchLead(id, { stage: target as BrokingLead["stage"] }, {
      type: "stage",
      title: `Moved to ${target}`,
      detail: "Stage updated on the pipeline board.",
      actor: currentActor,
    });
  }

  function confirmStage(updates: Partial<BrokingLead>, note: string) {
    if (!stageModal) return;
    patchLead(stageModal.lead.id, updates, {
      type: "stage",
      title: `Advanced to ${updates.stage}`,
      detail: note,
      actor: currentActor,
    });
    setStageModal(null);
  }

  function assignBranch(id: number, branchId: string) {
    const rm = rmsForBranch(branchId)[0];
    patchLead(id, { branch_id: branchId, rm_id: rm?.id ?? "" }, {
      type: "note",
      title: "Re-assigned branch",
      detail: `Moved to ${branchById(branchId)?.name} · RM ${rm?.name ?? "unassigned"}.`,
      actor: currentActor,
    });
  }

  function assignRm(id: number, rmId: string) {
    patchLead(id, { rm_id: rmId }, {
      type: "note",
      title: "Re-assigned RM",
      detail: `Now handled by ${rmById(rmId)?.name}.`,
      actor: currentActor,
    });
  }

  function autoAssignRoundRobin() {
    const pool = branchFilter === "ALL" ? RELATIONSHIP_MANAGERS : rmsForBranch(branchFilter);
    if (pool.length === 0) return;
    const unassignedFirst = filtered.filter((l) => l.stage === "New Enquiry");
    if (unassignedFirst.length === 0) return;
    setLeads((prev) =>
      prev.map((l) => {
        if (!unassignedFirst.some((u) => u.id === l.id)) return l;
        const rm = pool[roundRobinPtr.current % pool.length];
        roundRobinPtr.current += 1;
        return appendTimeline(
          { ...l, rm_id: rm.id, branch_id: rm.branchId },
          { type: "note", title: "Round-robin assignment", detail: `Auto-routed to ${rm.name} · ${branchById(rm.branchId)?.name}.`, actor: "System · Automation" }
        );
      })
    );
  }

  function toggleReminder(id: string) {
    setReminders((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: r.status === "done" ? "due" : "done" } : r))
    );
  }

  function openChatForLead(lead: BrokingLead) {
    setChat({ name: lead.name, phone: lead.phone, client_code: lead.client_code });
  }

  function resetDemo() {
    setIsResetting(true);
    setTimeout(() => {
      setLeads(cloneSeedLeads());
      setReminders(SEED_REMINDERS.map((r) => ({ ...r })));
      roundRobinPtr.current = 0;
      setIsResetting(false);
    }, 400);
  }

  /* ------------------------------ filtering ------------------------------ */
  const scoped = useMemo(
    () => (role === "RM" ? leads.filter((l) => l.rm_id === SELF_RM) : leads),
    [leads, role]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped.filter((l) => {
      if (branchFilter !== "ALL" && l.branch_id !== branchFilter) return false;
      if (rmFilter !== "ALL" && l.rm_id !== rmFilter) return false;
      if (segmentFilter !== "ALL" && !l.segments_active.includes(segmentFilter)) return false;
      if (stageFilter !== "ALL" && l.stage !== stageFilter) return false;
      if (priorityFilter !== "ALL" && l.priority !== priorityFilter) return false;
      if (q) {
        const hay = `${l.name} ${l.client_code} ${l.city} ${l.phone} ${l.email} ${l.pan}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [scoped, search, branchFilter, rmFilter, segmentFilter, stageFilter, priorityFilter]);

  const stats = useMemo(() => computeStats(filtered), [filtered]);
  const rmOptions = branchFilter === "ALL" ? RELATIONSHIP_MANAGERS : rmsForBranch(branchFilter);

  const TILES = [
    { label: "Total Enquiries", value: String(stats.totalEnquiries), sub: "In pipeline", icon: Users, color: "text-blue-600" },
    { label: "Accounts Opened", value: String(stats.accountsOpenedMtd), sub: "MTD (activated)", icon: UserCheck, color: "text-emerald-600" },
    { label: "KYC Pending", value: String(stats.kycPending), sub: "Onboarding queue", icon: FileClock, color: "text-amber-600" },
    { label: "Segment Activations", value: String(stats.segmentActivations), sub: "F&O · MCX · CDS · MTF", icon: Layers, color: "text-violet-600" },
    { label: "Active Clients", value: String(stats.activeClients), sub: "Funded & trading", icon: TrendingUp, color: "text-teal-600" },
    { label: "AUM", value: formatINRCompact(stats.aum), sub: "Assets under mgmt", icon: Wallet, color: "text-emerald-600" },
    { label: "Brokerage (MTD)", value: formatINRCompact(stats.brokerageMtd), sub: "Revenue this month", icon: Landmark, color: "text-sky-600" },
    { label: "Conversion", value: `${stats.conversion}%`, sub: "Enquiry → active", icon: Percent, color: "text-fuchsia-600" },
  ];

  const TABS: { id: ViewMode; label: string; icon: typeof KanbanSquare; accent: string }[] = [
    { id: "kanban", label: "Pipeline", icon: KanbanSquare, accent: "text-emerald-600" },
    { id: "table", label: "Clients", icon: TableIcon, accent: "text-blue-600" },
    { id: "reminders", label: "Reminders & Calls", icon: BellRing, accent: "text-amber-600" },
    { id: "reports", label: "MIS & Reports", icon: BarChart3, accent: "text-indigo-600" },
    { id: "compliance", label: "Compliance", icon: ShieldCheck, accent: "text-emerald-600" },
    { id: "ai-voice", label: "AI Voice", icon: Bot, accent: "text-teal-600" },
  ];

  const showFilters = view === "kanban" || view === "table";

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col text-slate-800">
      <StockBrokingTopBar onResetDemo={resetDemo} isResetting={isResetting} />

      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full space-y-5">
        {/* Title row + role switcher + simulate */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">Demat &amp; Trading Account CRM</h1>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Account-opening &amp; activation funnel · {role === "RM" ? `Viewing as ${rmById(SELF_RM)?.name}` : `${role} view · all branches`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Role switcher */}
            <div className="flex items-center bg-white border border-slate-200 rounded-xl p-1">
              {CRM_ROLES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRole(r.id)}
                  title={r.scope}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${role === r.id ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow" : "text-slate-500 hover:text-slate-900"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Simulate incoming message */}
            <button
              type="button"
              onClick={() => fireIncoming(true)}
              className="py-2 px-3.5 rounded-xl bg-white border border-emerald-500/30 hover:border-emerald-500/60 text-emerald-700 text-xs font-bold flex items-center gap-1.5 transition-all"
              title="Trigger a real-time incoming WhatsApp popup"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Simulate incoming
            </button>
          </div>
        </div>

        {/* Role scope banner */}
        {role === "RM" && (
          <div className="p-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-700 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            Restricted RM view — showing only leads assigned to <strong>{rmById(SELF_RM)?.name}</strong>. PAN is masked and MIS export is disabled.
          </div>
        )}

        {/* KPI tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3">
          {TILES.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.label} className="p-3.5 rounded-2xl bg-white border border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500 font-medium leading-tight">{t.label}</span>
                  <Icon className={`w-4 h-4 ${t.color}`} />
                </div>
                <div className="text-lg font-black text-slate-900 mt-1.5 truncate">{t.value}</div>
                <span className="text-[9px] text-slate-500">{t.sub}</span>
              </div>
            );
          })}
        </div>

        {/* View tabs */}
        <div className="flex items-center gap-1.5 p-1 rounded-xl bg-white border border-slate-200 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = view === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setView(t.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap transition-all ${on ? "bg-slate-100 text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
              >
                <Icon className={`w-3.5 h-3.5 ${on ? t.accent : ""}`} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        {showFilters && (
          <div className="p-3 rounded-2xl bg-white border border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2.5">
            <div className="relative lg:col-span-2">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, code, PAN, city…"
                className="w-full pl-9 pr-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-900 text-xs placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>
            <select
              value={branchFilter}
              onChange={(e) => { setBranchFilter(e.target.value); setRmFilter("ALL"); }}
              className="px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 text-xs focus:outline-none focus:border-emerald-500"
            >
              <option value="ALL">All Branches</option>
              {BRANCHES.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select
              value={rmFilter}
              onChange={(e) => setRmFilter(e.target.value)}
              disabled={role === "RM"}
              className="px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 text-xs focus:outline-none focus:border-emerald-500 disabled:opacity-50"
            >
              <option value="ALL">All RMs</option>
              {rmOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(e.target.value)}
              className="px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 text-xs focus:outline-none focus:border-emerald-500"
            >
              <option value="ALL">All Segments</option>
              {BROKING_SEGMENTS.map((s) => <option key={s.id} value={s.id}>{s.short}</option>)}
            </select>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 text-xs focus:outline-none focus:border-emerald-500"
            >
              <option value="ALL">All Stages</option>
              {BROKING_STAGES.map((s) => <option key={s.id} value={s.name}>{s.short}</option>)}
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-3 py-2 rounded-xl bg-white border border-slate-300 text-slate-700 text-xs focus:outline-none focus:border-emerald-500"
            >
              <option value="ALL">All Priority</option>
              <option value="HOT">🔥 Hot</option>
              <option value="WARM">🟡 Warm</option>
              <option value="COLD">❄️ Cold</option>
            </select>
          </div>
        )}

        {/* Round-robin toggle (kanban/table only) */}
        {showFilters && role !== "RM" && (
          <div className="flex flex-wrap items-center justify-between gap-3 -mt-1">
            <label className="flex items-center gap-2.5 text-xs text-slate-600 cursor-pointer">
              <button
                type="button"
                onClick={() => setRoundRobin((v) => !v)}
                className={`relative w-9 h-5 rounded-full transition-colors ${roundRobin ? "bg-emerald-500" : "bg-slate-200"}`}
                role="switch"
                aria-checked={roundRobin}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${roundRobin ? "translate-x-4" : ""}`} />
              </button>
              <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-emerald-600" /> Round-robin auto-assignment {branchFilter !== "ALL" && `(${branchById(branchFilter)?.city} pool)`}</span>
            </label>
            {roundRobin && (
              <button
                onClick={autoAssignRoundRobin}
                className="py-1.5 px-3 rounded-lg bg-emerald-500/10 hover:bg-emerald-500 text-emerald-700 hover:text-white text-[11px] font-bold border border-emerald-500/25 hover:border-transparent transition-all"
              >
                Distribute {filtered.filter((l) => l.stage === "New Enquiry").length} new enquiries now
              </button>
            )}
          </div>
        )}

        {/* Views */}
        {view === "kanban" && (
          <StockBrokingKanban
            leads={filtered}
            onSelectLead={setSelectedLead}
            onRequestStageAdvance={(lead, target) => setStageModal({ lead, target })}
            onDirectStageMove={directMove}
            onOpenChat={openChatForLead}
          />
        )}

        {view === "table" && (
          <ClientTable
            leads={filtered}
            role={role}
            roundRobin={roundRobin}
            onSelect={setSelectedLead}
            onOpenChat={openChatForLead}
            onAssignBranch={assignBranch}
            onAssignRm={assignRm}
            onStageChange={(id, target) => {
              const lead = filtered.find((l) => l.id === id);
              if (!lead) return;
              if (["Account Activated", "Segment Activation", "Active Trader"].includes(target)) {
                setStageModal({ lead, target });
              } else {
                directMove(id, target);
              }
            }}
          />
        )}

        {view === "reminders" && <RemindersPanel reminders={reminders} callLog={SEED_CALL_LOG} onToggleReminder={toggleReminder} />}
        {view === "reports" && <StockBrokingReportsTab leads={scoped} />}
        {view === "compliance" && <StockBrokingComplianceTab />}
        {view === "ai-voice" && <AiVoiceAssistantTab />}
      </div>

      {/* Overlays */}
      {stageModal && (
        <StockBrokingStageModal
          lead={stageModal.lead}
          targetStage={stageModal.target}
          onClose={() => setStageModal(null)}
          onConfirm={confirmStage}
        />
      )}
      {selectedLead && (
        <StockBrokingDetailModal
          lead={leads.find((l) => l.id === selectedLead.id) ?? selectedLead}
          role={role}
          onClose={() => setSelectedLead(null)}
          onOpenChat={(l) => { setSelectedLead(null); openChatForLead(l); }}
        />
      )}
      <StockBrokingWhatsAppDrawer contact={chat} isOpen={!!chat} onClose={() => setChat(null)} />
      <IncomingWhatsAppToast incoming={incoming} onOpenChat={openChatFromIncoming} onDismiss={() => setIncoming(null)} />
    </div>
  );
}

/* ----------------------------- Client table view ----------------------------- */

interface ClientTableProps {
  leads: BrokingLead[];
  role: CrmRole;
  roundRobin: boolean;
  onSelect: (lead: BrokingLead) => void;
  onOpenChat: (lead: BrokingLead) => void;
  onAssignBranch: (id: number, branchId: string) => void;
  onAssignRm: (id: number, rmId: string) => void;
  onStageChange: (id: number, target: string) => void;
}

function ClientTable({ leads, role, roundRobin, onSelect, onOpenChat, onAssignBranch, onAssignRm, onStageChange }: ClientTableProps) {
  if (leads.length === 0) {
    return (
      <div className="p-10 text-center text-slate-500 italic bg-white rounded-2xl border border-slate-200">
        No clients match the current filters.
      </div>
    );
  }

  return (
    <>
      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {leads.map((l) => (
          <div key={l.id} onClick={() => onSelect(l)} className="p-4 rounded-2xl bg-white border border-slate-200 active:scale-[0.99] transition-transform">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-emerald-600">{l.client_code}</span>
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${PRIORITY_STYLE[l.priority]}`}>{l.priority}</span>
            </div>
            <h4 className="text-sm font-bold text-slate-900 mt-1">{l.name}</h4>
            <p className="text-[11px] text-slate-500">{branchById(l.branch_id)?.name} · {rmById(l.rm_id)?.name ?? "Unassigned"}</p>
            <p className="text-[11px] text-slate-500 mt-1">{l.stage} · AUM {formatINRCompact(l.aum)}</p>
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-200" onClick={(e) => e.stopPropagation()}>
              <a href={`tel:${l.phone.replace(/\s/g, "")}`} className="p-2 rounded-lg bg-slate-100 text-emerald-600"><Phone className="w-3.5 h-3.5" /></a>
              <button onClick={() => onOpenChat(l)} className="p-2 rounded-lg bg-emerald-600 text-white"><MessageCircle className="w-3.5 h-3.5" /></button>
              <button onClick={() => onSelect(l)} className="ml-auto text-xs text-emerald-600 font-semibold flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Details</button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase font-semibold">
              <tr>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">PAN</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3">RM</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">AUM</th>
                <th className="px-4 py-3">Brokerage</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-slate-100/40 transition-colors cursor-pointer" onClick={() => onSelect(l)}>
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900 flex items-center gap-2">
                      {l.name}
                      <span className={`text-[8px] font-bold uppercase px-1 py-0.5 rounded border ${PRIORITY_STYLE[l.priority]}`}>{l.priority}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">{l.client_code} · {l.city}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-600">{maskPan(l.pan, role)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={l.branch_id}
                      disabled={role === "RM" || roundRobin}
                      onChange={(e) => onAssignBranch(l.id, e.target.value)}
                      className="px-2 py-1 rounded-lg bg-white border border-slate-300 text-slate-700 text-[11px] focus:outline-none focus:border-emerald-500 disabled:opacity-50 cursor-pointer"
                    >
                      {BRANCHES.map((b) => <option key={b.id} value={b.id}>{b.city}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={l.rm_id}
                      disabled={role === "RM" || roundRobin}
                      onChange={(e) => onAssignRm(l.id, e.target.value)}
                      className="px-2 py-1 rounded-lg bg-white border border-slate-300 text-slate-700 text-[11px] focus:outline-none focus:border-emerald-500 disabled:opacity-50 cursor-pointer max-w-[140px]"
                    >
                      {rmsForBranch(l.branch_id).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      {!rmsForBranch(l.branch_id).some((r) => r.id === l.rm_id) && l.rm_id && (
                        <option value={l.rm_id}>{rmById(l.rm_id)?.name}</option>
                      )}
                    </select>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={l.stage}
                      onChange={(e) => onStageChange(l.id, e.target.value)}
                      className="px-2 py-1 rounded-lg bg-white border border-slate-300 text-slate-700 text-[11px] focus:outline-none focus:border-emerald-500 cursor-pointer max-w-[150px]"
                    >
                      {BROKING_STAGES.map((s) => <option key={s.id} value={s.name}>{s.short}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{formatINRCompact(l.aum)}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-600">{formatINRCompact(l.brokerage_mtd)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <a href={`tel:${l.phone.replace(/\s/g, "")}`} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-500/10 transition-colors" title="Call"><Phone className="w-3.5 h-3.5" /></a>
                      <button onClick={() => onOpenChat(l)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-500/10 transition-colors" title="WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></button>
                      <button onClick={() => onSelect(l)} className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-200 transition-colors" title="Details"><Eye className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
